import { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  Calendar,
  Building,
  CalendarClock,
  Wrench,
} from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';

type TimePeriod = '30days' | '90days' | 'year' | 'all';

interface TimePeriodOption {
  value: TimePeriod;
  label: string;
}

const timePeriodOptions: TimePeriodOption[] = [
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

const COLORS = {
  primary: '#000000',
  secondary: '#4b5563',
  muted: '#9ca3af',
  border: '#e5e7eb',
  homes: '#2563eb',
  walkthroughs: '#7c3aed',
  maintenance: '#0d9488',
};

const UNASSIGNED_KEY = '__unassigned__';
const UNASSIGNED_LABEL = 'Unassigned';

interface TeamMember {
  id: string;
  full_name: string | null;
}

interface PerformanceRow {
  key: string;
  name: string;
  newHomes: number;
  walkthroughsScheduled: number;
  maintenanceScheduled: number;
  total: number;
}

interface LeadSourceRow {
  source: string;
  count: number;
}

const ReportsPerformance = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('year');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<PerformanceRow[]>([]);
  const [leadSourceRows, setLeadSourceRows] = useState<LeadSourceRow[]>([]);

  useEffect(() => {
    fetchPerformanceMetrics();
  }, [selectedPeriod]);

  const getDateFilter = (period: TimePeriod): Date | null => {
    const now = new Date();
    switch (period) {
      case '30days':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '90days':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case 'year':
        return new Date(now.getFullYear(), 0, 1);
      case 'all':
        return null;
    }
  };

  const fetchPerformanceMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateFilter = getDateFilter(selectedPeriod);
      const isoFrom = dateFilter ? dateFilter.toISOString() : null;

      // Fetch active team members (system managers)
      const { data: teamData, error: teamError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name', { ascending: true });
      if (teamError) throw teamError;

      const team: TeamMember[] = (teamData || []) as TeamMember[];

      const idToKey = new Map<string, string>();
      const keyToName = new Map<string, string>();

      team.forEach((member) => {
        idToKey.set(member.id, member.id);
        keyToName.set(member.id, member.full_name || 'Unnamed Team Member');
      });

      // Fetch homes created within the period
      let homesQuery = supabase
        .from('homes')
        .select('id, created_at, signed_up_by, lead_source');
      if (isoFrom) {
        homesQuery = homesQuery.gte('created_at', isoFrom);
      }
      const { data: homes, error: homesError } = await homesQuery;
      if (homesError) throw homesError;

      const homesByKey = new Map<string, number>();
      const homesByLeadSource = new Map<string, number>();
      (homes || []).forEach((home: {
        signed_up_by: string | null;
        lead_source: string | null;
      }) => {
        // Only attribute home sign-ups via the explicit signed_up_by field.
        // Homes without it are left as Unassigned so data gaps are visible.
        let key: string | null = null;
        if (home.signed_up_by && idToKey.has(home.signed_up_by)) {
          key = idToKey.get(home.signed_up_by) || null;
        }
        if (!key) key = UNASSIGNED_KEY;
        homesByKey.set(key, (homesByKey.get(key) || 0) + 1);

        const sourceLabel = (home.lead_source || '').trim() || 'Unknown';
        homesByLeadSource.set(sourceLabel, (homesByLeadSource.get(sourceLabel) || 0) + 1);
      });

      // Fetch visits created within the period (walkthroughs + maintenance),
      // excluding cancelled / no-show.
      let visitsQuery = supabase
        .from('visits')
        .select('id, visit_type, status, booked_by, created_at')
        .in('visit_type', ['walkthrough', 'maintenance'])
        .not('status', 'in', '(cancelled,no_show)');
      if (isoFrom) {
        visitsQuery = visitsQuery.gte('created_at', isoFrom);
      }
      const { data: visits, error: visitsError } = await visitsQuery;
      if (visitsError) throw visitsError;

      const walkthroughsByKey = new Map<string, number>();
      const maintenanceByKey = new Map<string, number>();

      (visits || []).forEach((visit: any) => {
        // Only attribute visits via the explicit booked_by field.
        // Visits without it are left as Unassigned so data gaps are visible.
        const key: string =
          visit.booked_by && idToKey.has(visit.booked_by)
            ? idToKey.get(visit.booked_by)!
            : UNASSIGNED_KEY;

        if (visit.visit_type === 'walkthrough') {
          walkthroughsByKey.set(key, (walkthroughsByKey.get(key) || 0) + 1);
        } else if (visit.visit_type === 'maintenance') {
          maintenanceByKey.set(key, (maintenanceByKey.get(key) || 0) + 1);
        }
      });

      // Build the unified key set — start with current team members, then add
      // any extra buckets surfaced by historical data.
      const allKeys = new Set<string>();
      team.forEach((m) => allKeys.add(m.id));
      [homesByKey, walkthroughsByKey, maintenanceByKey].forEach((map) => {
        map.forEach((_, k) => allKeys.add(k));
      });

      const computedRows: PerformanceRow[] = Array.from(allKeys).map((key) => {
        const newHomes = homesByKey.get(key) || 0;
        const walkthroughsScheduled = walkthroughsByKey.get(key) || 0;
        const maintenanceScheduled = maintenanceByKey.get(key) || 0;
        const name =
          key === UNASSIGNED_KEY
            ? UNASSIGNED_LABEL
            : keyToName.get(key) || 'Unknown';

        return {
          key,
          name,
          newHomes,
          walkthroughsScheduled,
          maintenanceScheduled,
          total: newHomes + walkthroughsScheduled + maintenanceScheduled,
        };
      });

      // Sort: team members first (alphabetical), then non-team buckets,
      // then Unassigned last.
      const teamIdSet = new Set(team.map((m) => m.id));
      computedRows.sort((a, b) => {
        const aIsUnassigned = a.key === UNASSIGNED_KEY;
        const bIsUnassigned = b.key === UNASSIGNED_KEY;
        if (aIsUnassigned !== bIsUnassigned) return aIsUnassigned ? 1 : -1;

        const aIsTeam = teamIdSet.has(a.key);
        const bIsTeam = teamIdSet.has(b.key);
        if (aIsTeam !== bIsTeam) return aIsTeam ? -1 : 1;

        return a.name.localeCompare(b.name);
      });

      setRows(computedRows);

      // Lead-source breakdown — sort with "Unknown" at the end and the rest
      // by descending count so the chart leads with the strongest channels.
      const computedLeadSourceRows: LeadSourceRow[] = Array.from(
        homesByLeadSource.entries()
      ).map(([source, count]) => ({ source, count }));
      computedLeadSourceRows.sort((a, b) => {
        if (a.source === 'Unknown' && b.source !== 'Unknown') return 1;
        if (b.source === 'Unknown' && a.source !== 'Unknown') return -1;
        return b.count - a.count;
      });
      setLeadSourceRows(computedLeadSourceRows);
    } catch (err: any) {
      console.error('Error fetching performance metrics:', err);
      setError(err?.message || 'Failed to load performance metrics');
      setRows([]);
      setLeadSourceRows([]);
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.newHomes += row.newHomes;
        acc.walkthroughsScheduled += row.walkthroughsScheduled;
        acc.maintenanceScheduled += row.maintenanceScheduled;
        return acc;
      },
      { newHomes: 0, walkthroughsScheduled: 0, maintenanceScheduled: 0 }
    );
  }, [rows]);

  const chartData = useMemo(
    () =>
      rows
        .filter((row) => row.total > 0)
        .map((row) => ({
          name: row.name,
          'New Homes': row.newHomes,
          Walkthroughs: row.walkthroughsScheduled,
          Maintenance: row.maintenanceScheduled,
        })),
    [rows]
  );

  const leadSourceChartData = useMemo(
    () =>
      leadSourceRows
        .filter((row) => row.count > 0)
        .map((row) => ({
          name: row.source,
          Homes: row.count,
        })),
    [leadSourceRows]
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading performance...</Text>
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.content}>
        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <TrendingUp size={24} color={COLORS.primary} />
            <View>
              <Text style={styles.title}>Team Performance</Text>
              <Text style={styles.subtitle}>
                KPIs for each stage of the customer success process
              </Text>
            </View>
          </View>
        </View>

        {/* Time Period Filter */}
        <View style={styles.filterContainer}>
          <View style={styles.filterHeader}>
            <Calendar size={20} color={COLORS.secondary} />
            <Text style={styles.filterLabel}>Time Period:</Text>
          </View>
          <View style={styles.filterButtons}>
            {timePeriodOptions.map((option) => (
              <Pressable
                key={option.value}
                onPress={() => setSelectedPeriod(option.value)}
                style={[
                  styles.filterButton,
                  selectedPeriod === option.value && styles.filterButtonActive,
                ]}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    selectedPeriod === option.value && styles.filterButtonTextActive,
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* Totals */}
        <View style={styles.statsRow}>
          <View style={[styles.statCard, { borderLeftColor: COLORS.homes }]}>
            <View style={styles.statCardHeader}>
              <Building size={18} color={COLORS.homes} />
              <Text style={styles.statLabel}>New Homes</Text>
            </View>
            <Text style={styles.statValue}>{totals.newHomes}</Text>
          </View>
          <View style={[styles.statCard, { borderLeftColor: COLORS.walkthroughs }]}>
            <View style={styles.statCardHeader}>
              <CalendarClock size={18} color={COLORS.walkthroughs} />
              <Text style={styles.statLabel}>Walkthroughs Scheduled</Text>
            </View>
            <Text style={styles.statValue}>{totals.walkthroughsScheduled}</Text>
          </View>
          <View style={[styles.statCard, { borderLeftColor: COLORS.maintenance }]}>
            <View style={styles.statCardHeader}>
              <Wrench size={18} color={COLORS.maintenance} />
              <Text style={styles.statLabel}>Maintenance Visits Scheduled</Text>
            </View>
            <Text style={styles.statValue}>{totals.maintenanceScheduled}</Text>
          </View>
        </View>

        {/* Per-team-member chart */}
        {chartData.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>By Team Member</Text>
            <View style={styles.chartContainer}>
              <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 60)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 24, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke={COLORS.secondary} allowDecimals={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 12 }}
                    stroke={COLORS.secondary}
                    width={140}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${COLORS.border}` }}
                  />
                  <Legend />
                  <Bar dataKey="New Homes" fill={COLORS.homes} />
                  <Bar dataKey="Walkthroughs" fill={COLORS.walkthroughs} />
                  <Bar dataKey="Maintenance" fill={COLORS.maintenance} />
                </BarChart>
              </ResponsiveContainer>
            </View>
          </View>
        )}

        {/* Per-team-member table */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Breakdown</Text>
          {rows.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                No activity in the selected time period.
              </Text>
            </View>
          ) : (
            <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderText, styles.colName]}>Team Member</Text>
                <Text style={[styles.tableHeaderText, styles.colNumber]}>New Homes</Text>
                <Text style={[styles.tableHeaderText, styles.colNumber]}>Walkthroughs</Text>
                <Text style={[styles.tableHeaderText, styles.colNumber]}>Maintenance</Text>
                <Text style={[styles.tableHeaderText, styles.colNumber]}>Total</Text>
              </View>
              {rows.map((row, index) => (
                <View
                  key={row.key}
                  style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}
                >
                  <Text style={[styles.tableCellText, styles.colName, styles.tableNameText]}>
                    {row.name}
                  </Text>
                  <Text style={[styles.tableCellText, styles.colNumber]}>{row.newHomes}</Text>
                  <Text style={[styles.tableCellText, styles.colNumber]}>
                    {row.walkthroughsScheduled}
                  </Text>
                  <Text style={[styles.tableCellText, styles.colNumber]}>
                    {row.maintenanceScheduled}
                  </Text>
                  <Text
                    style={[styles.tableCellText, styles.colNumber, styles.tableTotalText]}
                  >
                    {row.total}
                  </Text>
                </View>
              ))}
              <View style={[styles.tableRow, styles.tableTotalRow]}>
                <Text style={[styles.tableCellText, styles.colName, styles.tableTotalText]}>
                  Total
                </Text>
                <Text style={[styles.tableCellText, styles.colNumber, styles.tableTotalText]}>
                  {totals.newHomes}
                </Text>
                <Text style={[styles.tableCellText, styles.colNumber, styles.tableTotalText]}>
                  {totals.walkthroughsScheduled}
                </Text>
                <Text style={[styles.tableCellText, styles.colNumber, styles.tableTotalText]}>
                  {totals.maintenanceScheduled}
                </Text>
                <Text style={[styles.tableCellText, styles.colNumber, styles.tableTotalText]}>
                  {totals.newHomes +
                    totals.walkthroughsScheduled +
                    totals.maintenanceScheduled}
                </Text>
              </View>
            </View>
          )}
          <Text style={styles.helpText}>
            New homes are attributed first to {`"signed up by"`}, falling back
            to the assigned home manager for legacy rows. Visits are attributed
            first to {`"booked by"`}, then to who they are assigned to, then to
            the home manager. Counts include items created in the selected time
            period and exclude cancelled or no-show visits.
          </Text>
        </View>

        {/* Lead Source breakdown */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>New Homes by Lead Source</Text>
          {leadSourceChartData.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                No new homes in the selected time period.
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.chartContainer}>
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(220, leadSourceChartData.length * 48)}
                >
                  <BarChart
                    data={leadSourceChartData}
                    layout="vertical"
                    margin={{ left: 24, right: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 12 }}
                      stroke={COLORS.secondary}
                      allowDecimals={false}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fontSize: 12 }}
                      stroke={COLORS.secondary}
                      width={120}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: `1px solid ${COLORS.border}`,
                      }}
                    />
                    <Bar dataKey="Homes" fill={COLORS.homes} />
                  </BarChart>
                </ResponsiveContainer>
              </View>
              <View style={styles.tableContainer}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableHeaderText, styles.colName]}>Lead Source</Text>
                  <Text style={[styles.tableHeaderText, styles.colNumber]}>Homes</Text>
                </View>
                {leadSourceRows.map((row, index) => (
                  <View
                    key={row.source}
                    style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}
                  >
                    <Text style={[styles.tableCellText, styles.colName, styles.tableNameText]}>
                      {row.source}
                    </Text>
                    <Text style={[styles.tableCellText, styles.colNumber]}>{row.count}</Text>
                  </View>
                ))}
              </View>
            </>
          )}
          <Text style={styles.helpText}>
            {`Set a home's lead source on the Home Detail page under "Attribution".`}
          </Text>
        </View>
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    gap: 24,
    paddingBottom: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: 32,
  },
  loadingText: {
    fontSize: 16,
    color: COLORS.secondary,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 1,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.primary,
  },
  subtitle: {
    fontSize: 13,
    color: COLORS.secondary,
    marginTop: 2,
  },
  filterContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 16,
    gap: 12,
    ...shadows.sm,
  },
  filterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.secondary,
  },
  filterButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  filterButtonActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.secondary,
  },
  filterButtonTextActive: {
    color: '#ffffff',
  },
  errorContainer: {
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorText: {
    color: '#b91c1c',
    fontSize: 14,
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: 220,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#f3f4f6',
    borderLeftWidth: 4,
    gap: 8,
    ...shadows.sm,
  },
  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statLabel: {
    fontSize: 13,
    color: COLORS.secondary,
    fontWeight: '500',
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    color: COLORS.primary,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 20,
    gap: 16,
    ...shadows.sm,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.primary,
  },
  chartContainer: {
    marginTop: 4,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
  },
  emptyText: {
    fontSize: 14,
    color: COLORS.secondary,
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableRowEven: {
    backgroundColor: '#fafafa',
  },
  tableCellText: {
    fontSize: 14,
    color: COLORS.primary,
  },
  tableNameText: {
    fontWeight: '500',
  },
  tableTotalRow: {
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 0,
  },
  tableTotalText: {
    fontWeight: '700',
  },
  colName: {
    flex: 2,
    minWidth: 140,
  },
  colNumber: {
    flex: 1,
    minWidth: 80,
    textAlign: 'right',
  },
  helpText: {
    fontSize: 12,
    color: COLORS.muted,
    fontStyle: 'italic',
  },
});

export default ReportsPerformance;
